/**
 * Answer controls of every IELTS form (D-105), shared by the course, the daily review, the
 * placement, the challenge and the sittings: mcq (one choice), multi (choose N), gap (fields in
 * the sentence), tfng (3 buttons), match (drag a letter or pick it in a menu), order (drag or
 * move up / down), short (a few words) and write (editor with a word counter). The content stays
 * in English; the interface follows the learner's language.
 */

import { type Feedback, type Item, type ItemResponse, overWordLimit, splitGaps, tfngLabel, tfngOptions } from "@agencyos/core";
import { ArrowDown, ArrowUp, GripVertical } from "lucide-react";
import { type DragEvent, Fragment, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { countWords } from "../text";
import { useContentLang } from "./contentLang";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
export const letter = (index: number) => LETTERS[index] ?? String(index + 1);

interface Props {
  item: Item;
  value: ItemResponse;
  onChange: (value: ItemResponse) => void;
  disabled?: boolean;
  /** The correction: marks the right and wrong choices. */
  feedback?: Feedback | null;
  /** Visible label of the group (the prompt is shown by the caller). */
  labelledBy?: string;
}

const asList = (value: ItemResponse): (number | string | null)[] => (Array.isArray(value) ? value : []);

function expectedIndexes(feedback: Feedback | null | undefined): number[] {
  if (!feedback) return [];
  const answer = feedback.answer;
  if (typeof answer === "number") return [answer];
  return Array.isArray(answer) ? answer.filter((a): a is number => typeof a === "number") : [];
}

function Choices({ item, value, onChange, disabled, feedback, labelledBy }: Props) {
  const lang = useContentLang();
  const { t } = useTranslation();
  const name = useId();
  const multi = item.type === "multi";
  const chosen = multi ? asList(value).filter((v): v is number => typeof v === "number") : typeof value === "number" ? [value] : [];
  const right = expectedIndexes(feedback);
  const full = multi && chosen.length >= item.choose;
  const toggle = (index: number) => {
    if (!multi) return onChange(index);
    onChange(chosen.includes(index) ? chosen.filter((c) => c !== index) : [...chosen, index].sort((a, b) => a - b));
  };
  return (
    <fieldset className="plain-fieldset ac-options-set" disabled={disabled} aria-labelledby={labelledBy}>
      {multi && (
        <p className="ac-footnote ac-item__rule">
          {t("ielts.item.chooseN", { count: item.choose })} · {t("ielts.item.chosen", { n: chosen.length, total: item.choose })}
        </p>
      )}
      <div className={`ac-options${item.options.length === 4 ? " ac-options--quad" : ""}`} lang={lang}>
        {item.options.map((option, index) => {
          const on = chosen.includes(index);
          const mark = feedback ? (right.includes(index) ? " ac-option--right" : on ? " ac-option--wrong" : "") : "";
          return (
            <label key={index} className={`ac-option${on ? " ac-option--on" : ""}${mark}`}>
              <input
                type={multi ? "checkbox" : "radio"}
                name={name}
                checked={on}
                disabled={disabled || (full && !on)}
                onChange={() => toggle(index)}
              />
              <span className="ac-option__letter" aria-hidden>
                {letter(index)}
              </span>
              <span className="ac-option__text">{option}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

/** The sentence with a field at each « ___ ». */
function Gaps({ item, value, onChange, disabled }: Props) {
  const lang = useContentLang();
  const { t } = useTranslation();
  const pieces = splitGaps(item.prompt);
  const answers = asList(value);
  const set = (index: number, text: string) => {
    const next = pieces.slice(1).map((_, i) => String(answers[i] ?? ""));
    next[index] = text;
    onChange(next);
  };
  return (
    <p className="ac-gap-text" lang={lang}>
      {pieces.map((piece, index) => (
        <Fragment key={index}>
          {piece}
          {index < pieces.length - 1 && (
            <input
              className="ac-gap-input"
              type="text"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              disabled={disabled}
              value={String(answers[index] ?? "")}
              aria-label={t("ielts.item.blank", { n: index + 1, total: pieces.length - 1 })}
              size={Math.max(8, String(answers[index] ?? "").length + 2)}
              onChange={(e) => set(index, e.target.value)}
            />
          )}
        </Fragment>
      ))}
    </p>
  );
}

function TrueFalse({ item, value, onChange, disabled, feedback, labelledBy }: Props) {
  const lang = useContentLang();
  const expected = typeof feedback?.answer === "string" ? feedback.answer.toUpperCase() : "";
  return (
    <div className="ac-tfng" role="radiogroup" aria-labelledby={labelledBy} lang={lang}>
      {tfngOptions(item.tfngLabels).map((option) => {
        const on = value === option;
        const mark = feedback ? (option === expected ? " ac-tfng__btn--right" : on ? " ac-tfng__btn--wrong" : "") : "";
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled}
            className={`ac-tfng__btn${on ? " ac-tfng__btn--on" : ""}${mark}`}
            onClick={() => onChange(option)}
          >
            {tfngLabel(option, lang, item.tfngLabels)}
          </button>
        );
      })}
    </div>
  );
}

/** Matching: each item on the left gets a letter, dragged from the bank or picked in its menu. */
function Matching({ item, value, onChange, disabled, feedback }: Props) {
  const lang = useContentLang();
  const { t } = useTranslation();
  const answers = asList(value);
  const right = expectedIndexes(feedback);
  const [over, setOver] = useState<number | null>(null);
  const set = (row: number, choice: number | null) => {
    const next = item.left.map((_, i) => {
      const v = answers[i];
      return typeof v === "number" ? v : null;
    });
    next[row] = choice;
    onChange(next);
  };
  const onDrop = (row: number) => (event: DragEvent<HTMLLIElement>) => {
    event.preventDefault();
    setOver(null);
    const choice = Number(event.dataTransfer.getData("text/plain"));
    if (!Number.isNaN(choice) && !disabled) set(row, choice);
  };
  // Letters alone (A–H of a plan) are listed as a bank; long options keep their text.
  const short = item.right.every((r) => r.length <= 2);
  return (
    <div className="ac-match" lang={lang}>
      {!disabled && (
        <ul className="ac-match__bank" aria-label={t("ielts.item.bank")}>
          {item.right.map((option, index) => (
            <li
              key={index}
              className="ac-match__chip"
              draggable
              onDragStart={(event) => event.dataTransfer.setData("text/plain", String(index))}
            >
              <GripVertical aria-hidden size={13} />
              {short ? option : <><strong>{letter(index)}</strong> {option}</>}
            </li>
          ))}
        </ul>
      )}
      <ol className="ac-match__rows">
        {item.left.map((label, row) => {
          const chosen = answers[row];
          const mark = feedback ? (typeof chosen === "number" && chosen === right[row] ? " ac-match__row--right" : " ac-match__row--wrong") : "";
          return (
            <li
              key={row}
              className={`ac-match__row${over === row ? " ac-match__row--over" : ""}${mark}`}
              onDragOver={(event) => {
                event.preventDefault();
                setOver(row);
              }}
              onDragLeave={() => setOver(null)}
              onDrop={onDrop(row)}
            >
              <span className="ac-match__label">
                {item.numbers[row] !== undefined && <span className="ac-qnum ao-num">{item.numbers[row]}</span>}
                {label}
              </span>
              <select
                className="ao-field__input ac-match__select"
                value={typeof chosen === "number" ? String(chosen) : ""}
                disabled={disabled}
                aria-label={t("ielts.item.matchFor", { item: label })}
                onChange={(e) => set(row, e.target.value === "" ? null : Number(e.target.value))}
              >
                <option value="">{t("ielts.item.pick")}</option>
                {item.right.map((option, index) => (
                  <option key={index} value={index}>
                    {short ? option : `${letter(index)} · ${option}`}
                  </option>
                ))}
              </select>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** Ordering: drag a line, or move it with the arrows (keyboard and touch friendly). */
function Ordering({ item, value, onChange, disabled, feedback }: Props) {
  const lang = useContentLang();
  const { t } = useTranslation();
  const order = asList(value).filter((v): v is number => typeof v === "number");
  const current = order.length === item.items.length ? order : item.items.map((_, i) => i);
  const [dragged, setDragged] = useState<number | null>(null);
  const move = (from: number, to: number) => {
    if (to < 0 || to >= current.length || from === to) return;
    const next = [...current];
    const [picked] = next.splice(from, 1);
    if (picked === undefined) return;
    next.splice(to, 0, picked);
    onChange(next);
  };
  const expected = expectedIndexes(feedback);
  return (
    <ol className="ac-order" lang={lang} aria-label={t("ielts.item.orderLabel")}>
      {current.map((index, position) => {
        const mark = feedback ? (expected[position] === index ? " ac-order__row--right" : " ac-order__row--wrong") : "";
        return (
          <li
            key={index}
            className={`ac-order__row${dragged === position ? " ac-order__row--drag" : ""}${mark}`}
            draggable={!disabled}
            onDragStart={() => setDragged(position)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              if (dragged !== null) move(dragged, position);
              setDragged(null);
            }}
            onDragEnd={() => setDragged(null)}
          >
            <span className="ac-order__n ao-num" aria-hidden="true">
              {position + 1}
            </span>
            <span className="ac-order__text">{item.items[index]}</span>
            {!disabled && (
              <span className="ac-order__moves">
                <button
                  type="button"
                  className="ac-icon-button ac-icon-button--small"
                  disabled={position === 0}
                  onClick={() => move(position, position - 1)}
                  aria-label={t("ielts.item.moveUp", { item: item.items[index] ?? "" })}
                >
                  <ArrowUp aria-hidden size={15} />
                </button>
                <button
                  type="button"
                  className="ac-icon-button ac-icon-button--small"
                  disabled={position === current.length - 1}
                  onClick={() => move(position, position + 1)}
                  aria-label={t("ielts.item.moveDown", { item: item.items[index] ?? "" })}
                >
                  <ArrowDown aria-hidden size={15} />
                </button>
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function ShortAnswer({ item, value, onChange, disabled }: Props) {
  const lang = useContentLang();
  const { t } = useTranslation();
  const text = typeof value === "string" ? value : "";
  const over = overWordLimit(text, item.maxWords, item.andNumber);
  return (
    <div className="ac-short">
      <input
        className="ao-field__input ac-short__input"
        type="text"
        lang={lang}
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        value={text}
        aria-label={t("ielts.item.yourAnswer")}
        aria-invalid={over || undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {over && <p className="ac-footnote ac-item__over">{t("ielts.item.tooManyWords", { count: item.maxWords ?? 0 })}</p>}
    </div>
  );
}

/** Free writing (course « write »): the editor and its word counter against the minimum. */
export function WritingField({
  value,
  onChange,
  minWords,
  disabled,
  rows = 8,
  label,
}: {
  value: string;
  onChange: (text: string) => void;
  minWords: number | null;
  disabled?: boolean;
  rows?: number;
  label: string;
}) {
  const lang = useContentLang();
  const { t } = useTranslation();
  const id = useId();
  const words = countWords(value);
  const reached = minWords === null || words >= minWords;
  const ratio = minWords ? Math.min(1, words / minWords) : 0;
  return (
    <div className={`ac-editor${disabled ? " ac-editor--locked" : ""}`}>
      <label className="ac-editor__field">
        <span className="visually-hidden">{label}</span>
        <textarea
          lang={lang}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="sentences"
          rows={rows}
          value={value}
          readOnly={disabled}
          aria-describedby={id}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
      <div className="ac-editor__status">
        <div className={`ac-words ac-words--${minWords === null ? "none" : reached ? "ok" : "short"}`}>
          <p id={id} className="ac-words__text ao-num" aria-live="polite">
            {minWords !== null
              ? t("ielts.writing.words", { count: words, min: minWords })
              : t("academy.writing.wordsPlain", { count: words })}
            {!reached && ` · ${t("ielts.writing.belowMin")}`}
          </p>
          {minWords !== null && (
            <span className="ac-words__meter" aria-hidden="true">
              <span className="ac-words__fill" style={{ transform: `scaleX(${ratio})` }} />
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export function ItemInput(props: Props) {
  const { t } = useTranslation();
  const { item } = props;
  switch (item.type) {
    case "mcq":
    case "multi":
      return <Choices {...props} />;
    case "gap":
      return (
        <div className="ac-item__gaps">
          <Gaps {...props} />
          {item.maxWords !== null && <p className="ac-footnote ac-item__rule">{t(item.andNumber ? "ielts.item.maxWordsNumber" : "ielts.item.maxWords", { count: item.maxWords })}</p>}
        </div>
      );
    case "tfng":
      return <TrueFalse {...props} />;
    case "match":
      return <Matching {...props} />;
    case "order":
      return <Ordering {...props} />;
    case "short":
      return (
        <>
          {item.maxWords !== null && <p className="ac-footnote ac-item__rule">{t(item.andNumber ? "ielts.item.maxWordsNumber" : "ielts.item.maxWords", { count: item.maxWords })}</p>}
          <ShortAnswer {...props} />
        </>
      );
    case "write":
      return (
        <WritingField
          value={typeof props.value === "string" ? props.value : ""}
          onChange={(text) => props.onChange(text)}
          minWords={item.minWords}
          disabled={props.disabled}
          label={t("ielts.item.yourAnswer")}
        />
      );
    default:
      return null;
  }
}
